/**
 * Page de gestion des appels de ban pour l'admin
 * 
 * Permet aux administrateurs de:
 * - Voir la liste des appels en attente
 * - Marquer un appel comme en cours d'examen
 * - Approuver ou rejeter un appel
 * - Voir les statistiques des appels
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  RateReview as ReviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import openAttachment from '../../utils/attachmentUtils';
import restrictionAdminService from '../../services/restrictionAdminService';

const BanAppealsPage = () => {
  const [appeals, setAppeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastResolved, setLastResolved] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Dialog de traitement d'appel
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [appealUserInfo, setAppealUserInfo] = useState(null); // 🆕 Info utilisateur (nom, langue, etc.)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [decision, setDecision] = useState('approved');
  const [responseMessage, setResponseMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Détails étendus
  const [expandedAppeal, setExpandedAppeal] = useState(null);
  const { token } = useAuth();

  /**
   * Charger les appels en attente
   */
  const loadPendingAppeals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/appeals/admin/pending', {
        params: { page, limit, status: statusFilter }
      });
      
      if (response.data.success) {
        setAppeals(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Erreur chargement appels:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des appels');
    } finally {
      setLoading(false);
    }
  };

  // Re-load when filter changes
  useEffect(() => {
    setPage(1);
    loadPendingAppeals();
  }, [statusFilter]);

  /**
   * Charger les statistiques des appels
   */
  const loadStats = async () => {
    try {
      const response = await api.get('/appeals/admin/stats');
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  /**
   * Marquer un appel comme en cours d'examen
   */
  const handleMarkUnderReview = async (appealId) => {
    try {
      setError(null);
      const response = await api.post(`/appeals/admin/${appealId}/review`);
      
      if (response.data.success) {
        setSuccess('Appel marqué comme en cours d\'examen');
        loadPendingAppeals();
        loadStats();
      }
    } catch (err) {
      console.error('Erreur marquage en examen:', err);
      setError(err.response?.data?.message || 'Erreur lors du marquage');
    }
  };

  /**
   * Ouvrir le dialog de traitement
   */
  const handleOpenDialog = async (appeal) => {
    setSelectedAppeal(appeal);
    setDialogOpen(true);
    setDecision('approved');
    setResponseMessage('');
    
    // 🆕 Charger les infos utilisateur
    const userId = appeal?.User?.id || appeal?.userId || appeal?.user_id;
    if (userId) {
      try {
        const response = await restrictionAdminService.getUserBasicInfo(userId);
        setAppealUserInfo(response.data);
      } catch (err) {
        console.error('Failed to load user info:', err);
        setAppealUserInfo(null);
      }
    }
  };

  /**
   * Fermer le dialog de traitement
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAppeal(null);
    setAppealUserInfo(null); // 🆕 Reset user info
    setDecision('approved');
    setResponseMessage('');
  };

  /**
   * Traiter un appel (approuver/rejeter)
   */
  const handleResolveAppeal = async () => {
    if (!selectedAppeal) return;
    if (!responseMessage.trim() || responseMessage.length < 10) {
      setError('Le message de réponse doit contenir au moins 10 caractères');
      return;
    }
    
    try {
      setProcessing(true);
      setError(null);
      
      const response = await api.post(`/appeals/admin/${selectedAppeal.id}/resolve`, {
        decision,
        responseMessage: responseMessage.trim()
      });

      // Alignement comportemental: l'action "Approuver un appeal" doit appliquer
      // exactement la même levée de ban que l'action "Débannir" (UserRestrictionsPage).
      // Le endpoint appeals peut lever le ban, mais ne réactive pas forcément les listings.
      // Le endpoint admin/unban, lui, est la référence (réactivation listings incluse).
      if (response.data.success && decision === 'approved') {
        const userId = selectedAppeal?.User?.id || selectedAppeal?.userId || selectedAppeal?.user_id;
        if (userId) {
          try {
            await restrictionAdminService.unbanUser(userId, `Appeal approuvé: ${responseMessage.trim()}`);
          } catch (e) {
            // Ne pas bloquer la résolution de l'appel si l'alignement unban échoue,
            // mais remonter un message explicite.
            console.error('Erreur alignement unban après approbation appeal:', e);
            setError(
              e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                'Appel approuvé mais échec lors de la levée de ban (alignement action débannir)'
            );
          }
        } else {
          console.warn('Impossible de déterminer le userId pour exécuter unban après approbation appeal', selectedAppeal);
        }
      }
      
      if (response.data.success) {
        const resolvedStatus = decision === 'approved' ? 'approved' : 'rejected';
        const resolvedAt = new Date().toISOString();

        // Mise à jour immédiate UI (temps réel)
        setAppeals(prev => prev.map(a => (
          a.id === selectedAppeal.id
            ? { ...a, status: resolvedStatus, responseMessage: responseMessage.trim(), resolvedAt }
            : a
        )));

        setLastResolved({
          id: selectedAppeal.id,
          decision: resolvedStatus,
          user: selectedAppeal.User,
          responseMessage: responseMessage.trim(),
          resolvedAt
        });

        setSuccess(
          resolvedStatus === 'approved'
            ? `Appel approuvé (ban levé) — ${selectedAppeal.User?.email || selectedAppeal.User?.name || selectedAppeal.id}`
            : `Appel rejeté — ${selectedAppeal.User?.email || selectedAppeal.User?.name || selectedAppeal.id}`
        );

        handleCloseDialog();

        // Refresh différé pour ne pas "faire disparaître" la ligne avant que l'admin voie le résultat
        setTimeout(() => {
          loadPendingAppeals();
          loadStats();
        }, 1200);
      }
    } catch (err) {
      console.error('Erreur traitement appel:', err);
      setError(err.response?.data?.message || 'Erreur lors du traitement de l\'appel');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Obtenir la couleur du statut
   */
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      under_review: 'info',
      approved: 'success',
      rejected: 'error'
    };
    return colors[status] || 'default';
  };

  /**
   * Obtenir le label du statut
   */
  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      under_review: 'En examen',
      approved: 'Approuvé',
      rejected: 'Rejeté'
    };
    return labels[status] || status;
  };

  /**
   * Basculer les détails étendus
   */
  const toggleExpanded = (appealId) => {
    setExpandedAppeal(expandedAppeal === appealId ? null : appealId);
  };

  // Charger les données au montage et quand la page change
  useEffect(() => {
    loadPendingAppeals();
    loadStats();
  }, [page]);

  // Auto-dismiss des messages de succès
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (!lastResolved) return;
    const timer = setTimeout(() => setLastResolved(null), 10000);
    return () => clearTimeout(timer);
  }, [lastResolved]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        📋 Gestion des Appels de Ban
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {lastResolved && (
        <Alert
          severity={lastResolved.decision === 'approved' ? 'success' : 'warning'}
          onClose={() => setLastResolved(null)}
          sx={{ mb: 2 }}
        >
          <AlertTitle>Résultat appliqué</AlertTitle>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              color={lastResolved.decision === 'approved' ? 'success' : 'error'}
              label={lastResolved.decision === 'approved' ? 'Approuvé' : 'Rejeté'}
            />
            <Typography variant="body2">
              {lastResolved.user?.email || lastResolved.user?.name || 'Utilisateur'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              (ID: {lastResolved.id})
            </Typography>
          </Stack>
        </Alert>
      )}

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Appels
                </Typography>
                <Typography variant="h5">
                  {stats.totalAppeals || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Attente
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {stats.pendingAppeals || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approuvés
                </Typography>
                <Typography variant="h5" color="success.main">
                  {stats.approvedAppeals || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Rejetés
                </Typography>
                <Typography variant="h5" color="error.main">
                  {stats.rejectedAppeals || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Filtre
                </Typography>
                  <Tabs
                    value={statusFilter}
                    onChange={(e, v) => setStatusFilter(v)}
                    variant="fullWidth"
                  >
                    <Tab label="En attente" value="pending" />
                    <Tab label="Traitée récemment" value="processed_recent" />
                    <Tab label="Tous" value="all" />
                  </Tabs>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Liste des appels */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Appels en Attente de Traitement
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : appeals.length === 0 ? (
          <Alert severity="info">
            Aucun appel en attente
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px"></TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Date Soumission</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Raison (extrait)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appeals.map((appeal) => (
                    <React.Fragment key={appeal.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(appeal.id)}
                          >
                            {expandedAppeal === appeal.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {appeal.User?.name || 'Utilisateur'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {appeal.User?.email || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {appeal.submittedAt 
                            ? format(new Date(appeal.submittedAt), 'dd MMM yyyy HH:mm', { locale: fr })
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(appeal.status)}
                            color={getStatusColor(appeal.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {appeal.reason?.substring(0, 80)}...
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {appeal.status === 'pending' && (
                              <Tooltip title="Marquer en examen">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleMarkUnderReview(appeal.id)}
                                >
                                  <ReviewIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Traiter l'appel">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(appeal)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Ligne de détails étendus */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expandedAppeal === appeal.id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, my: 1 }}>
                              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                Détails de l'appel
                              </Typography>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Raison complète:
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                    {appeal.reason}
                                  </Typography>
                                </Grid>
                                
                                {appeal.additionalInfo && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="textSecondary">
                                      Informations additionnelles:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                      {appeal.additionalInfo}
                                    </Typography>
                                  </Grid>
                                )}

                                {(appeal.attachments || []).length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary">
                                      Documents joints:
                                    </Typography>
                                    <List dense>
                                      {appeal.attachments.map((att) => (
                                        <ListItem
                                          key={att.id}
                                          secondaryAction={
                                            <Button
                                              size="small"
                                              onClick={() => openAttachment(appeal.id, att.id, token).catch(err => console.error(err))}
                                            >
                                              Ouvrir
                                            </Button>
                                          }
                                        >
                                          <ListItemText
                                            primary={att.originalName || att.filename}
                                            secondary={`${att.mimeType || ''}${att.size ? ` • ${Math.round(att.size / 1024)} KB` : ''}`}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Grid>
                                )}
                                
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Restriction ID:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {appeal.restrictionId}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Deadline:
                                  </Typography>
                                  <Typography variant="body2">
                                    {appeal.deadline 
                                      ? format(new Date(appeal.deadline), 'dd MMMM yyyy HH:mm', { locale: fr })
                                      : 'N/A'
                                    }
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Dialog de traitement d'appel */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Traiter l'Appel de Ban
        </DialogTitle>
        <DialogContent>
          {selectedAppeal && (
            <Box sx={{ pt: 1 }}>
              {/* 🆕 Afficher les infos utilisateur si disponibles */}
              {appealUserInfo && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Utilisateur:</strong> {appealUserInfo.name || appealUserInfo.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {appealUserInfo.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Langue de préférence:</strong> {appealUserInfo.language.toUpperCase()}
                  </Typography>
                </Alert>
              )}
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Utilisateur: {selectedAppeal.User?.name} ({selectedAppeal.User?.email})
                </Typography>
                <Typography variant="caption">
                  Soumis le {format(new Date(selectedAppeal.submittedAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Raison de l'appel:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedAppeal.reason}
                </Typography>
                
                {selectedAppeal.additionalInfo && (
                  <>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                      Informations additionnelles:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAppeal.additionalInfo}
                    </Typography>
                  </>
                )}

                {(selectedAppeal.attachments || []).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                      Documents joints:
                    </Typography>
                    <List dense>
                      {selectedAppeal.attachments.map((att) => (
                        <ListItem
                          key={att.id}
                          secondaryAction={
                            <Button
                              size="small"
                              onClick={() => openAttachment(selectedAppeal.id, att.id, token).catch(err => console.error(err))}
                            >
                              Ouvrir
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={att.originalName || att.filename}
                            secondary={`${att.mimeType || ''}${att.size ? ` • ${Math.round(att.size / 1024)} KB` : ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Paper>

              <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Décision:
                </Typography>
                <RadioGroup
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                >
                  <FormControlLabel 
                    value="approved" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          Approuver l'appel
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Le ban sera levé et l'utilisateur sera notifié
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="rejected" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          Rejeter l'appel
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Le ban restera en place et l'utilisateur sera notifié
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Message de réponse"
                multiline
                rows={4}
                fullWidth
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Expliquez votre décision à l'utilisateur..."
                helperText={appealUserInfo 
                  ? `${responseMessage.length}/2000 caractères (minimum 10) - Cette réponse sera envoyée à l'utilisateur dans sa langue (${appealUserInfo.language.toUpperCase()})`
                  : `${responseMessage.length}/2000 caractères (minimum 10)`
                }
                required
                error={responseMessage.length > 0 && responseMessage.length < 10}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>
            Annuler
          </Button>
          <Button 
            onClick={handleResolveAppeal}
            variant="contained"
            color={decision === 'approved' ? 'success' : 'error'}
            disabled={processing || !responseMessage.trim() || responseMessage.length < 10}
            startIcon={processing ? <CircularProgress size={20} /> : decision === 'approved' ? <ApproveIcon /> : <RejectIcon />}
          >
            {processing ? 'Traitement...' : decision === 'approved' ? 'Approuver' : 'Rejeter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BanAppealsPage;
